import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import {
  announcementPopupDismissed,
  announcementsEnqueued,
} from '@features/announcements/model/slice';
import { useListAnnouncementsQuery } from '@shared/api';
import { playAnnouncementChime } from '@shared/lib/notifySound';
import type { Announcement } from '@entities/announcement';

interface Props {
  /** Открыть общую модалку «Что нового» по центру экрана (состояние держит AppShell). */
  onOpen: () => void;
  /** Подавить авто-открытие, пока модалка «Что нового» уже открыта. */
  suppressed: boolean;
}

/**
 * Сколько ждём, прежде чем счесть нерезолвимую голову очереди «мёртвым» id и
 * снять её. Даёт рефетчу ленты (после `invalidateTags` на `announcement_published`)
 * долететь и подтвердить карточку — с запасом на сетевой round-trip к бэку.
 */
const DEAD_ID_GRACE_MS = 4000;

/**
 * Контроллер авто-показа модалки «Что нового» при выходе обновлений и перезагрузке
 * страницы. Вместо отдельной одиночной карточки сразу открывает центральную
 * модалку `UpdatesModal` с вкладками по агентам (где вкладка агента пользователя
 * развёрнута, а вкладки других агентов свёрнуты).
 *
 * Пометка `popup_seen_at` (`POST /announcements/{id}/seen`) отправляется при
 * закрытии `UpdatesModal` крестиком / кнопкой «Понятно» / Esc.
 */
export function UpdatesPopup({ onOpen, suppressed }: Props) {
  const dispatch = useAppDispatch();
  const queue = useAppSelector((s) => s.announcements.popupQueue);
  const { data: announcements = [], isLoading, isFetching } = useListAnnouncementsQuery();

  // id анонсов, для которых уже проиграли «дзинь» в этой сессии.
  const chimedFor = useRef<Set<string>>(new Set());

  const headId = queue[0] ?? null;
  const current: Announcement | null = headId
    ? (announcements.find((a) => a.id === headId) ?? null)
    : null;

  // Наполнение очереди из ленты: опубликованные анонсы, которые пользователь
  // ещё не видел в модалке и которые не убраны из ленты.
  useEffect(() => {
    const pending = announcements
      .filter((a) => a.published_at != null && a.popup_seen_at == null && a.cleared_at == null)
      .sort((a, b) => (a.published_at ?? '').localeCompare(b.published_at ?? ''))
      .map((a) => a.id);
    if (pending.length > 0) dispatch(announcementsEnqueued(pending));
  }, [announcements, dispatch]);

  // Защита от «мёртвого» id в голове очереди.
  useEffect(() => {
    if (!headId || current || isLoading || isFetching) return;
    const timer = setTimeout(() => dispatch(announcementPopupDismissed()), DEAD_ID_GRACE_MS);
    return () => clearTimeout(timer);
  }, [headId, current, isLoading, isFetching, dispatch]);

  // Когда в очереди есть непосмотренное обновление — автоматически открываем
  // центральную модалку «Что нового» с вкладками по агентам и играем мягкий звук.
  useEffect(() => {
    if (suppressed || !current || !headId) return;
    const id = headId;
    const chimeWhenVisible = () => {
      if (document.visibilityState !== 'visible' || chimedFor.current.has(id)) return;
      chimedFor.current.add(id);
      playAnnouncementChime();
    };
    chimeWhenVisible();
    onOpen();
    document.addEventListener('visibilitychange', chimeWhenVisible);
    return () => document.removeEventListener('visibilitychange', chimeWhenVisible);
  }, [suppressed, current, headId, onOpen]);

  return null;
}
